import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { useAppDispatch, useAppSelector } from 'store';

import { openAuthModal } from 'modules/auth';
import { globalConst, isBrowser } from 'modules/global';

interface IStoreItem {
  title: string;
  logo: string;
  description: string;
  url: string;
}

const HomeBanner = () => {
  const { push } = useRouter();
  const dispatch = useAppDispatch();
  const t = useTranslations('home.banner');
  const user = useAppSelector(state => state.user.phone);
  const baseSocialMedias = [
    {
      title: t('akam_instagramda'),
      logo: '/images/instagram.webp',
      description: t('akam_instagramda_desc'),
      url: `${process.env.NEXT_PUBLIC_INSTAGRAM}`
    },
    {
      title: t('akam_telegramda'),
      logo: '/images/telegram.webp',
      description: t('akam_telegramda_desc'),
      url: `${process.env.NEXT_PUBLIC_TELEGRAM}`
    }
  ];
  const [socialMedias, setSocialMedias] = useState<IStoreItem | null>(null);

  const onOpenPage = () => {
    if (user) {
      return push(globalConst.pageNames.TOPIC_TEST);
    }

    return dispatch(openAuthModal());
  };

  useEffect(() => {
    const userAgent = isBrowser() ? navigator.userAgent : '';
    const isIOS = /iPhone|iPad|Macintosh/.test(userAgent);
    let storeItem: IStoreItem | null = null;

    if (isIOS) {
      storeItem = {
        title: 'App Store',
        logo: '/images/appstore.webp',
        description: t('iphone_uchun_ilovamizni_yuklab_oling'),
        url: 'https://apps.apple.com/de/app/akam-onlayn-testlar/id1619248324'
      };
    } else {
      storeItem = {
        title: 'Google Play',
        logo: '/images/playmarket.webp',
        description: t('android_uchun_ilovamizni_yuklab_oling'),
        url: 'https://play.google.com/store/apps/details?id=com.test.aim.uz'
      };
    }

    if (storeItem) {
      setSocialMedias(storeItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="text-center">
      <h1 className="text-3xl font-bold md:text-3xl">{t('title')}</h1>
      <div className="flex justify-center">
        <p className="my-4 max-w-lg px-5 text-sm font-medium md:px-0">{t('desc')}</p>
      </div>
      <button
        onClick={onOpenPage}
        className="min-w-55 cursor-pointer mt-6 inline-block rounded-lg bg-green-500 px-7 py-2.5 text-sm font-medium text-white transition-transform duration-200 hover:scale-105"
        aria-label={t(user ? 'test_ishlashni_boshlang' : 'royxatdan_otish')}
      >
        {t(user ? 'test_ishlashni_boshlang' : 'royxatdan_otish')}
      </button>
      <ul className="relative mx-auto mt-10 flex max-w-[830px] items-center justify-center gap-4">
        {baseSocialMedias?.map((item, index) => (
          <li
            // eslint-disable-next-line react/no-array-index-key
            key={index}
          >
            <Link
              target="_blank"
              href={item?.url}
              className="flex items-center gap-2 rounded-xl bg-white p-2 transition-transform duration-200 hover:scale-105"
            >
              <div className="relative aspect-[1.18] w-10">
                <Image fill src={item.logo} alt={item.title} className="object-contain" />
              </div>
              <div className="hidden text-start text-gray-800 md:block">
                <p className="text-[0.8rem] font-medium">{item.title}</p>
                <p className="text-[0.7rem]">{item.description}</p>
              </div>
            </Link>
          </li>
        ))}
        <li>
          {socialMedias ? (
            <Link
              // eslint-disable-next-line react/no-array-index-key
              target="_blank"
              href={socialMedias?.url}
              className="flex items-center gap-2 rounded-xl bg-white p-2 transition-transform duration-200 hover:scale-105"
            >
              <div className="relative h-9 w-10">
                <Image fill src={socialMedias?.logo} alt={socialMedias?.title} />
              </div>

              <div className="hidden text-start text-gray-800 md:block">
                <p className="text-[0.8rem] font-medium">{socialMedias?.title}</p>
                <p className="text-[0.7rem]">{socialMedias?.description}</p>
              </div>
            </Link>
          ) : (
            <div className="h-12 w-14 animate-pulse rounded-xl bg-gray-200 md:w-[16.280rem]" />
          )}
        </li>
      </ul>
    </div>
  );
};

export default HomeBanner;
